import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { EnrichmentMatchStatus, EnrichmentProviderName } from '@hp/domain';
import { invalidateAll } from '../../api/queryClient';
import { trpc, type RouterOutputs } from '../../api/trpc';
import { isRunActive } from '../../lib/runs';

export type EnrichmentRun = RouterOutputs['enrichment']['listRuns'][number];
export type EnrichmentStats = RouterOutputs['enrichment']['stats'];
export type EnrichmentMatch = RouterOutputs['enrichment']['listMatches']['items'][number];
export type AresCandidate = EnrichmentMatch['candidates'][number];
export type FirmographicCandidate = EnrichmentMatch['profiles'][number];
export type PeopleOrg = RouterOutputs['enrichment']['people']['searchOrgs']['items'][number];
export type PeopleCandidate = RouterOutputs['enrichment']['people']['candidates'][number];

/** Enrichment runs, polled every 5 s while one is queued/running. */
export function useEnrichmentRuns(enabled = true) {
  return useQuery(
    trpc.enrichment.listRuns.queryOptions(undefined, {
      enabled,
      refetchInterval: (query) => (query.state.data?.some((r) => isRunActive(r.status)) ? 5000 : false),
    }),
  );
}

export function useEnrichmentStats(enabled: boolean, poll: boolean) {
  return useQuery(trpc.enrichment.stats.queryOptions(undefined, { enabled, refetchInterval: poll ? 5000 : false }));
}

export function useEnrichmentMatches(status: EnrichmentMatchStatus, provider?: EnrichmentProviderName, limit = 50) {
  return useInfiniteQuery(
    trpc.enrichment.listMatches.infiniteQueryOptions(
      { status, provider, limit },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
}

export function usePeopleOrgSearch(q: string) {
  return useQuery(
    trpc.enrichment.people.searchOrgs.queryOptions({ q }, { enabled: q.trim().length >= 2, staleTime: 30_000 }),
  );
}

export function usePeopleCandidates(orgPdId: number | null) {
  return useQuery(
    trpc.enrichment.people.candidates.queryOptions({ orgPdId: orgPdId ?? 0 }, { enabled: orgPdId !== null }),
  );
}

/** Runs, stats and matches changed — and with them the guided pipeline's view of enrichment. */
export function invalidateEnrichment(): Promise<void> {
  return invalidateAll(trpc.enrichment.pathFilter(), trpc.pipeline.pathFilter());
}

/** People finder: org search counts and candidate lists. */
export function invalidatePeople(): Promise<void> {
  return invalidateAll(trpc.enrichment.people.pathFilter());
}
